import { getPaddleBillingFunction } from '@easy-expense/auth-client';
import { useAuthStore } from '@easy-expense/data-firestore-client';
import { WebEnv } from '@easy-expense/env-mobile';
import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import {
  MAX_TEAM_SEATS,
  TEAM_ANNUAL_DISCOUNT,
  TEAM_BASE_SEAT_PRICE,
  TEAM_MIN_SEATS,
} from '@easy-expense/utils-shared';
import React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { CheckoutBackLink } from '../components/Checkout/CheckoutBackLink.component';
import { CheckoutBrandRow } from '../components/Checkout/CheckoutBrandRow.component';
import { CheckoutLayout } from '../components/Checkout/CheckoutLayout.component';
import { CheckoutTotals } from '../components/Checkout/CheckoutTotals.component';
import { usePaddleCheckout } from '../hooks/usePaddleCheckout';
import {
  CheckoutSummary,
  formatAmount,
  formatRecurring,
  withQuantity,
} from '../lib/Billing/checkoutSummary';
import {
  isWebCheckoutSource,
  parseTeamCheckoutRequest,
  resolveTeamCheckoutPriceID,
  teamCheckoutDestinations,
  teamPurchaseFloor,
  TeamCheckoutRequest,
} from '../lib/Billing/teamCheckout';

/**
 * How long a web checkout waits for the auth session to restore before giving up on it. The auth
 * store starts `currentUser` at null and never says whether it has finished restoring, so "signed
 * out" and "still restoring" look identical -- without a deadline a signed-out visitor would wait
 * on a session that is never coming.
 */
const AUTH_RESTORE_TIMEOUT_MS = 8000;

/**
 * What this buyer may be sold, resolved before checkout opens. Web checkouts answer it from the
 * buyer's real billing state; handoffs cannot ask and take the defaults below.
 */
type CheckoutOffer = {
  /** Whether a trial-bearing price may be sold -- see Trial Eligibility. */
  trialEligible: boolean;
  /** Purchase Floor: the fewest seats this buyer may choose. */
  floor: number;
};

/**
 * What a checkout is offered when its buyer's billing state cannot be read: no session (every
 * mobile handoff), a session belonging to someone else, or a lookup that failed. Deliberately the
 * trial variant -- the alternative is charging a genuinely new customer immediately after
 * promising them a trial, which is worse than the seat of a returning one going untaxed.
 */
const UNVERIFIED_OFFER: CheckoutOffer = { trialEligible: true, floor: TEAM_MIN_SEATS };

/**
 * Figures shown before Paddle reports its own: the Team list price for the requested term in
 * USD, so the summary is never a wall of placeholders. Replaced by the live, tax-inclusive
 * numbers (in the buyer's currency) as soon as the inline checkout loads.
 *
 * What is due today follows Trial Eligibility, and defaults to the full price while the offer is
 * still unresolved. Quoting $0 before we know a trial is being sold would promise the buyer
 * something the checkout may be about to charge them for.
 */
function estimateFromRequest(
  request: TeamCheckoutRequest,
  seats: number,
  offer: CheckoutOffer | null,
): CheckoutSummary {
  const annual = request.term === 'annual';
  const perSeat = annual
    ? TEAM_BASE_SEAT_PRICE * 12 * (1 - TEAM_ANNUAL_DISCOUNT)
    : TEAM_BASE_SEAT_PRICE;
  const subtotal = perSeat * seats;
  return {
    currencyCode: 'USD',
    quantity: seats,
    unitPrice: perSeat,
    subtotal,
    discount: 0,
    tax: 0,
    dueToday: offer?.trialEligible === true ? 0 : subtotal,
    recurringTotal: subtotal,
    billingInterval: annual ? 'year' : 'month',
    trialEndsOn: null,
    repricing: false,
  };
}

function clampSeats(value: number, floor: number): number {
  return Math.min(Math.max(Math.floor(value), floor), MAX_TEAM_SEATS);
}

const StepButton: React.FC<{
  icon: 'add' | 'remove';
  label: string;
  disabled: boolean;
  onClick: () => void;
}> = ({ icon, label, disabled, onClick }) => (
  <Layout.PressableColumn
    center
    align
    bg="white"
    px={10}
    py={8}
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    style={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.35 : 1 }}
  >
    <Icon name={icon} size={18} color={theme.colors.primary} />
  </Layout.PressableColumn>
);

/** Quantity stepper for seats: minus, editable number, plus. Clamped to the plan's seat range. */
const SeatStepper: React.FC<{
  seats: number;
  floor: number;
  onChange: (seats: number) => void;
}> = ({ seats, floor, onChange }) => {
  const [draft, setDraft] = React.useState(String(seats));
  React.useEffect(() => {
    setDraft(String(seats));
  }, [seats]);

  // Clamping can leave the parent's seat count unchanged (typing 1 when already at the minimum),
  // and then the [seats] effect never fires -- so the box is reset from the clamped value here
  // rather than waiting on a round trip that will not happen.
  const commitDraft = () => {
    const typed = Number(draft);
    if (draft.trim() === '' || !Number.isFinite(typed)) {
      setDraft(String(seats));
      return;
    }
    const clamped = clampSeats(typed, floor);
    setDraft(String(clamped));
    onChange(clamped);
  };

  return (
    <Layout.Row align border={[1, 'solid', 'grayLight']} radius={8} style={{ overflow: 'hidden' }}>
      <StepButton
        icon="remove"
        label={getTranslation('Remove a seat')}
        disabled={seats <= floor}
        onClick={() => onChange(clampSeats(seats - 1, floor))}
      />
      <OpenSans.Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        aria-label={getTranslation('Number of seats')}
        value={draft}
        weight="bold-700"
        size="s-16"
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value.replace(/[^0-9]/g, ''))
        }
        onBlur={commitDraft}
        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }
        }}
        style={{ width: 44, textAlign: 'center', padding: 0 }}
      />
      <StepButton
        icon="add"
        label={getTranslation('Add a seat')}
        disabled={seats >= MAX_TEAM_SEATS}
        onClick={() => onChange(clampSeats(seats + 1, floor))}
      />
    </Layout.Row>
  );
};

const NOWRAP = { whiteSpace: 'nowrap', flexShrink: 0 } as const;

/**
 * The summary pane: what the buyer is getting and what it costs, top-down. The brand row, then
 * the Team plan as a line item with its seat stepper, then the totals.
 */
const OrderSummary: React.FC<{
  figures: CheckoutSummary;
  /** The buyer's chosen seat count, which leads `figures.quantity` while a re-price is in flight. */
  seats: number;
  /** Purchase Floor -- the fewest seats this buyer may choose. */
  floor: number;
  live: boolean;
  isProduction: boolean;
  compact: boolean;
  onSeatsChange: (seats: number) => void;
}> = ({ figures, seats, floor, live, isProduction, compact, onSeatsChange }) => {
  const eachLine = `${formatAmount(figures.unitPrice, figures.currencyCode)} ${getTranslation('each')}`;
  const seatMinimum = `${floor}-${getTranslation('seat minimum')}`;
  const stale = figures.repricing;

  return (
    <Layout.Column>
      <CheckoutBrandRow isProduction={isProduction} />
      <Spacer.Vertical size={compact ? 32 : 56} />

      <Layout.Row align="flex-start">
        <Layout.Column
          align
          center
          bg="brandPrimaryXLight"
          radius={8}
          style={{ width: 44, height: 44, flexShrink: 0 }}
        >
          <Icon name="people" size={22} color={theme.colors.brandPrimary} />
        </Layout.Column>
        <Spacer.Horizontal size={14} />
        <Layout.Column grow style={{ minWidth: 0 }}>
          <Layout.Row align="flex-start" justify="space-between">
            <OpenSans.Primary size="s-16" weight="bold-700">
              {getTranslation('Team plan')}
            </OpenSans.Primary>
            <Spacer.Horizontal size={12} />
            <Layout.Column align="flex-end" style={{ flexShrink: 0 }}>
              <OpenSans.Custom size="s-16" color={stale ? 'secondary' : 'primary'} style={NOWRAP}>
                {formatRecurring(figures.subtotal, figures)}
              </OpenSans.Custom>
              <OpenSans.Secondary size="xs-12" style={NOWRAP}>
                {eachLine}
              </OpenSans.Secondary>
            </Layout.Column>
          </Layout.Row>
          <Spacer.Vertical size={14} />
          <Layout.Row align>
            <OpenSans.Secondary size="xs-12">{getTranslation('Seats')}</OpenSans.Secondary>
            <Spacer.Horizontal size={8} />
            <SeatStepper seats={seats} floor={floor} onChange={onSeatsChange} />
            <Spacer.Horizontal size={8} />
            <OpenSans.Secondary size="xs-12">{seatMinimum}</OpenSans.Secondary>
          </Layout.Row>
        </Layout.Column>
      </Layout.Row>

      <Spacer.Vertical size={28} />

      <CheckoutTotals figures={figures} live={live} />
    </Layout.Column>
  );
};

/**
 * The Team Checkout: every Team plan purchase passes through here, from mobile (EZ-5238) and from
 * ee-web's own entry points (EZ-5279). Binds the purchase to the `userID` in the URL rather than a
 * web session -- the Paddle webhook resolves the buyer's Org Admin organizations from that ID
 * alone, so an ee-web login is never load-bearing even where one happens to exist.
 *
 * A session is used for one thing only, and only when the Checkout Source says the buyer came from
 * inside ee-web: resolving what they may be sold -- Trial Eligibility and the Purchase Floor --
 * from their real billing state instead of from anything the URL claims. See ADR 0008.
 *
 * The split-pane shell, the Paddle lifecycle and the totals are shared with the Individual
 * Checkout; what lives here is the seat count, which is the whole of what makes this checkout
 * different from one that sells a fixed plan.
 */
export const TeamCheckoutPage: React.FC = () => {
  const [seats, setSeats] = React.useState(TEAM_MIN_SEATS);
  const [offer, setOffer] = React.useState<CheckoutOffer | null>(null);
  const [authRestoreExpired, setAuthRestoreExpired] = React.useState(false);
  const navigate = useNavigate();
  const currentUser = useAuthStore.use.currentUser();
  const [params] = useSearchParams();
  const request = React.useMemo(() => parseTeamCheckoutRequest(params), [params]);
  const isProduction = WebEnv.buildType === 'production';

  React.useEffect(() => {
    if (request) {
      setSeats(request.seats);
    }
  }, [request]);

  const isWebCheckout = isWebCheckoutSource(request?.source ?? null);

  // Deadline on waiting for the session, since a signed-out visitor's `currentUser` stays null
  // forever and is indistinguishable from one that has not restored yet.
  React.useEffect(() => {
    if (!isWebCheckout || currentUser) {
      return;
    }
    const timer = setTimeout(() => setAuthRestoreExpired(true), AUTH_RESTORE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isWebCheckout, currentUser]);

  /**
   * Resolves what this buyer may be sold. A handoff takes the defaults immediately; a web checkout
   * waits for its session and asks the billing callable, which takes identity from the caller's
   * auth token and verifies workspace membership itself -- so nothing in the URL is trusted, and a
   * session belonging to someone other than the requested buyer is treated as no session at all.
   */
  // Keyed on the request's own values rather than the parsed object, so a re-render that hands
  // back a new object can never re-issue the lookup behind it.
  const requestedUserID = request?.userID;
  const requestedWorkspaceID = request?.workspaceID;
  React.useEffect(() => {
    if (!requestedUserID) {
      return;
    }
    if (!isWebCheckout) {
      setOffer(UNVERIFIED_OFFER);
      return;
    }
    const uid = currentUser?.uid;
    if (!uid && !authRestoreExpired) {
      return;
    }
    if (uid !== requestedUserID) {
      setOffer(UNVERIFIED_OFFER);
      return;
    }
    let cancelled = false;
    getPaddleBillingFunction()({ workspaceID: requestedWorkspaceID ?? undefined })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        // An active price of any kind means an existing subscriber, who reaches in-place billing
        // rather than this page; hasEverSubscribed catches the returning one who has lapsed.
        setOffer({
          trialEligible: data.activePriceID === null && !data.hasEverSubscribed,
          floor: teamPurchaseFloor(data.activeSeats),
        });
      })
      .catch((error) => {
        console.error('ERROR resolving team checkout offer: ', error);
        if (!cancelled) {
          setOffer(UNVERIFIED_OFFER);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [requestedUserID, requestedWorkspaceID, isWebCheckout, currentUser?.uid, authRestoreExpired]);

  // The floor can land above the seat count the request asked for (a workspace with more members
  // than the link was built with), and the buyer must never be shown a figure they cannot buy.
  React.useEffect(() => {
    if (!offer) {
      return;
    }
    setSeats((current) => Math.max(current, offer.floor));
  }, [offer]);

  // Null until the offer resolves, which is what stops checkout opening on a trial price the
  // buyer may not be owed. Paddle re-prices in place when the seat count changes.
  let items = null;
  if (request && offer) {
    items = [
      {
        quantity: seats,
        priceId: resolveTeamCheckoutPriceID(request.term, isProduction, offer.trialEligible),
      },
    ];
  }

  const { status, summary, retry } = usePaddleCheckout({
    items,
    email: request?.email ?? '',
    customData: { userID: request?.userID ?? '', checkoutSource: request?.source ?? null },
    successPath: teamCheckoutDestinations(request?.source ?? null).success,
  });

  if (!request) {
    return <Navigate to="/404" replace />;
  }

  const live = summary !== null;
  const figures = withQuantity(summary ?? estimateFromRequest(request, seats, offer), seats);
  const cancelPath = teamCheckoutDestinations(request.source).cancel;
  // Until the offer lands the floor is unknown; the plan-wide minimum is its lower bound, so the
  // stepper is never briefly looser than what the buyer will actually be held to.
  const floor = offer?.floor ?? TEAM_MIN_SEATS;

  return (
    <CheckoutLayout
      status={status}
      onRetry={retry}
      summary={(compact) => (
        <>
          {cancelPath ? (
            <>
              <CheckoutBackLink onPress={() => navigate(cancelPath)} />
              <Spacer.Vertical size={16} />
            </>
          ) : null}
          <OrderSummary
            figures={figures}
            seats={seats}
            floor={floor}
            live={live}
            isProduction={isProduction}
            compact={compact}
            onSeatsChange={setSeats}
          />
        </>
      )}
    />
  );
};
